import type { Profile } from './profiles';

/**
 * Office worker profile with specific project details
 */
export const officeProfile: Profile = {
  name: 'Office',
  minMeetingsPerDay: 2,
  averageSwitchingFrequencySeconds: 360, // Switch context every ~6 minutes on average
  meetings: [
    {
      title: 'Westfield Industries - Status Update',
      durationMinutes: 60,
      entries: [
        { tool: 'microsoft_excel', title: 'Project Timeline - Westfield Industries.xlsx' },
        { tool: 'microsoft_word', title: 'Meeting Notes - Westfield Implementation.docx' },
        { tool: 'microsoft_outlook', title: 'RE: Westfield Project Status' },
        { tool: 'sharepoint', title: 'Client Documentation - Westfield Industries' },
      ],
      aiNotes: {
        concise: 'Reviewing implementation progress with client project team.',
        detailed:
          'Meeting with Westfield Industries team to discuss project timeline, document implementation progress, reference previous email communications, and update client documentation in SharePoint.',
      },
    },
    {
      title: 'Department All-Hands Meeting',
      durationMinutes: 45,
      entries: [
        { tool: 'microsoft_powerpoint', title: 'Department Performance - Q2 2024.pptx' },
        { tool: 'microsoft_word', title: 'Meeting Notes - Department Meeting.docx' },
        { tool: 'sharepoint', title: 'Department Plans and Policies' },
      ],
      aiNotes: {
        concise: 'Attending department-wide meeting on quarterly performance.',
        detailed:
          'Participating in department all-hands meeting, viewing quarterly performance presentation, taking detailed meeting notes, and referencing department plans and policies documentation.',
      },
    },
    {
      title: 'Project Kickoff: Atlas Group',
      durationMinutes: 90,
      entries: [
        { tool: 'microsoft_powerpoint', title: 'Onboarding Presentation - Atlas Group.pptx' },
        { tool: 'microsoft_word', title: 'Meeting Notes - Atlas Group Onboarding.docx' },
        { tool: 'microsoft_excel', title: 'Project Timeline - Atlas Group.xlsx' },
        { tool: 'sharepoint', title: 'Client Documentation - Atlas Group' },
      ],
      aiNotes: {
        concise: 'Launching new client implementation project with timeline planning.',
        detailed:
          'Facilitating project kickoff meeting for Atlas Group, presenting onboarding information, documenting detailed meeting notes, reviewing project timeline, and organizing client documentation in SharePoint.',
      },
    },
    {
      title: 'Budget Review Meeting',
      durationMinutes: 60,
      entries: [
        { tool: 'microsoft_excel', title: 'Department Budget - Q2 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Budget Variance Analysis.xlsx' },
        { tool: 'microsoft_word', title: 'Budget Variance Report - June 2024.docx' },
      ],
      aiNotes: {
        concise: 'Analyzing quarterly budget performance and variances.',
        detailed:
          'Reviewing department budget for Q2 2024, analyzing budget variances, examining financial performance data, and preparing formal variance reports for June 2024.',
      },
    },
    {
      title: '1:1 with Mark',
      durationMinutes: 30,
      entries: [
        { tool: 'microsoft_word', title: 'Project Status Updates.docx' },
        { tool: 'microsoft_excel', title: 'Resource Allocation - Q3 2024.xlsx' },
        { tool: 'microsoft_outlook', title: 'Calendar' },
      ],
      aiNotes: {
        concise: 'One-on-one meeting discussing project status and resource planning.',
        detailed:
          'Meeting with Mark to discuss project status updates, planning Q3 resource allocation, and coordinating calendar schedules for upcoming project activities.',
      },
    },
    {
      title: 'HR Meeting: Quarterly Reviews',
      durationMinutes: 45,
      entries: [
        { tool: 'microsoft_word', title: 'Performance Review Template - 2024.docx' },
        { tool: 'microsoft_excel', title: 'Team Performance Metrics - Q2.xlsx' },
        { tool: 'sharepoint', title: 'Human Resources - Policies and Procedures' },
      ],
      aiNotes: {
        concise: 'Preparing for upcoming team performance evaluation process.',
        detailed:
          'Meeting with HR to discuss quarterly performance review process, examining performance review templates, reviewing team metrics from Q2, and referencing HR policies and procedures for the evaluation process.',
      },
    },
    {
      title: 'Client Call: Carter Healthcare',
      durationMinutes: 30,
      entries: [
        { tool: 'microsoft_word', title: 'Requirements Document - Carter Healthcare.docx' },
        { tool: 'microsoft_excel', title: 'Client Requirements Matrix - Q2 2024.xlsx' },
        { tool: 'microsoft_outlook', title: 'FW: Carter Healthcare Follow-up' },
      ],
      aiNotes: {
        concise: 'Discussing implementation requirements with healthcare client.',
        detailed:
          'Conducting call with Carter Healthcare to review requirements documentation, updating client requirements matrix, and following up on previous email communications about implementation details.',
      },
    },
  ],
  sessions: [
    {
      name: 'Client Documentation',
      predominantTool: 'microsoft_word',
      entries: [
        { tool: 'microsoft_word', title: 'Client Proposal - Westfield Industries.docx' },
        { tool: 'microsoft_word', title: 'Service Agreement - Pinnacle Solutions.docx' },
        { tool: 'microsoft_word', title: 'Meeting Notes - Atlas Group Onboarding.docx' },
        { tool: 'microsoft_word', title: 'Requirements Document - Carter Healthcare.docx' },
        { tool: 'microsoft_excel', title: 'Client Requirements Matrix - Q2 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Project Timeline - Westfield Industries.xlsx' },
        { tool: 'sharepoint', title: 'Client Documentation - Westfield Industries' },
        { url: 'https://sharepoint.com', title: 'SharePoint - Document repository access' },
        { url: 'https://salesforce.com', title: 'Salesforce - Customer account information' },
        { url: 'https://hoovers.com', title: 'Hoovers - Client company research' },
      ],
      aiNotes: {
        concise: 'Preparing client proposals, agreements, and project documentation.',
        detailed:
          'Creating and updating client documentation including proposals for Westfield Industries, service agreements for Pinnacle Solutions, meeting notes for Atlas Group, and requirements documents for Carter Healthcare. Organizing client information in SharePoint and retrieving account details from Salesforce.',
      },
    },
    {
      name: 'Financial Reporting',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Department Budget - Q2 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Expense Report - June 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Revenue Forecast - H2 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Project Profitability Analysis.xlsx' },
        { tool: 'microsoft_excel', title: 'Client Invoicing Status - June 2024.xlsx' },
        { tool: 'microsoft_word', title: 'Budget Variance Report - June 2024.docx' },
        { tool: 'sharepoint', title: 'Finance Department - Monthly Reports' },
        { url: 'https://youtube.com', title: 'YouTube - Microsoft Excel: Advanced PivotTables' },
        { url: 'https://google.com', title: 'Google search: Excel VLOOKUP with multiple criteria' },
        { url: 'https://concur.com', title: 'Concur - Expense report submission' },
      ],
      aiNotes: {
        concise: 'Creating financial reports and analyzing budget data.',
        detailed:
          'Preparing quarterly budget reports, tracking monthly expenses, forecasting revenue for H2 2024, analyzing project profitability, monitoring client invoicing status, and drafting budget variance reports. Researching advanced Excel techniques for improved financial analysis.',
      },
    },
    {
      name: 'General Communication',
      predominantTool: 'microsoft_teams',
      entries: [
        { tool: 'microsoft_teams', title: 'Chat | Mark | Microsoft Teams' },
        { tool: 'microsoft_teams', title: 'Chat | Susan | Microsoft Teams' },
        { tool: 'microsoft_outlook', title: 'Mail: Atlas Group Implementation Timeline' },
        { tool: 'microsoft_outlook', title: 'Mail: Budget Approval for Carter Healthcare' },
        { tool: 'microsoft_outlook', title: 'Mail: Weekly Status Report Distribution' },
        { url: 'https://google.com', title: 'Google search: Professional email templates' },
      ],
      aiNotes: {
        concise: 'Coordinating with team members and clients via messaging and email.',
        detailed:
          'Communicating with colleagues through Microsoft Teams chats, sending and responding to emails about Atlas Group implementation timeline, processing budget approvals for Carter Healthcare, distributing weekly status reports, and researching professional email templates.',
      },
    },
    {
      name: 'Presentation Preparation',
      predominantTool: 'microsoft_powerpoint',
      entries: [
        {
          tool: 'microsoft_powerpoint',
          title: 'Client Quarterly Review - Westfield Industries.pptx',
        },
        {
          tool: 'microsoft_powerpoint',
          title: 'New Service Offering - Executive Presentation.pptx',
        },
        { tool: 'microsoft_powerpoint', title: 'Department Performance - Q2 2024.pptx' },
        { tool: 'microsoft_powerpoint', title: 'Onboarding Presentation - Atlas Group.pptx' },
        { tool: 'microsoft_excel', title: 'Presentation Data - Client Growth Metrics.xlsx' },
        { tool: 'microsoft_word', title: 'Presentation Speaking Notes - Executive Review.docx' },
        { url: 'https://google.com', title: 'Google search: PowerPoint animations best practices' },
      ],
      aiNotes: {
        concise: 'Creating presentations for client reviews and executive meetings.',
        detailed:
          'Developing PowerPoint presentations for Westfield Industries quarterly review, new service offering executive presentation, department performance report, and Atlas Group onboarding. Compiling supporting data on client growth metrics and preparing speaker notes for executive review.',
      },
    },
    {
      name: 'HR Administration',
      predominantTool: 'microsoft_word',
      entries: [
        { tool: 'microsoft_word', title: 'Job Description - Senior Account Manager.docx' },
        { tool: 'microsoft_word', title: 'Performance Review Template - 2024.docx' },
        { tool: 'microsoft_excel', title: 'Team Vacation Schedule - Q3 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Training Completion Tracking.xlsx' },
        { tool: 'microsoft_teams', title: 'Chat | HR Team | Microsoft Teams' },
        { tool: 'sharepoint', title: 'Human Resources - Policies and Procedures' },
        { tool: 'jira', title: 'HR-127: Update employee handbook for remote work policy' },
        { url: 'https://indeed.com', title: 'Indeed - Senior Account Manager job descriptions' },
        { url: 'https://linkedin.com', title: 'LinkedIn - Industry networking' },
      ],
      aiNotes: {
        concise: 'Managing HR documentation and personnel processes.',
        detailed:
          'Creating job descriptions for senior account manager position, preparing performance review templates, maintaining team vacation schedules, tracking employee training completion, coordinating with HR team, updating employee handbook for remote work policy, and researching industry job descriptions.',
      },
    },
    {
      name: 'Administrative Tasks',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Travel Expense Report - Boston Conference.xlsx' },
        { tool: 'microsoft_excel', title: 'Office Supply Inventory.xlsx' },
        { tool: 'microsoft_excel', title: 'Meeting Room Scheduling - July 2024.xlsx' },
        { tool: 'microsoft_word', title: 'Department Process Documentation.docx' },
        { tool: 'microsoft_teams', title: 'Chat | Admin Team | Microsoft Teams' },
        { tool: 'microsoft_outlook', title: 'Calendar: July Planning Meetings' },
        { tool: 'sharepoint', title: 'Administrative Templates and Forms' },
        { url: 'https://office.com', title: 'Microsoft 365 - Admin Portal' },
      ],
      aiNotes: {
        concise: 'Managing office operations and administrative processes.',
        detailed:
          'Processing travel expense reports for Boston conference, maintaining office supply inventory, coordinating meeting room schedules, updating department process documentation, communicating with administrative team, scheduling July planning meetings, and accessing administrative templates and forms.',
      },
    },
    {
      name: 'Client Research',
      predominantTool: 'google_chrome',
      entries: [
        { url: 'https://hoovers.com', title: 'Hoovers - Client company research' },
        { url: 'https://linkedin.com', title: 'LinkedIn - Client company profiles' },
        { url: 'https://linkedin.com', title: 'LinkedIn - Potential client stakeholders' },
        { url: 'https://salesforce.com', title: 'Salesforce - Customer history review' },
        { url: 'https://zoominfo.com', title: 'ZoomInfo - Company contact information' },
        { url: 'https://dnb.com', title: 'Dun & Bradstreet - Company financial data' },
        { url: 'https://bloomberg.com', title: 'Bloomberg - Industry news and trends' },
        { tool: 'microsoft_word', title: 'Client Research Notes - Potential Opportunities.docx' },
        { tool: 'microsoft_word', title: 'Client Research - Competitor Analysis.docx' },
        { tool: 'microsoft_excel', title: 'Client Comparison Analysis.xlsx' },
        {
          tool: 'microsoft_powerpoint',
          title: 'Client Research Findings - Sales Team Presentation.pptx',
        },
        { tool: 'microsoft_outlook', title: 'RE: Client Research Summary' },
      ],
      aiNotes: {
        concise: 'Gathering intelligence on client companies and stakeholders.',
        detailed:
          'Researching client companies through industry databases, reviewing company profiles, identifying key stakeholders, examining customer history, collecting contact information, analyzing financial data, monitoring industry news, documenting potential opportunities, conducting competitor analysis, and preparing presentation of research findings.',
      },
    },
    {
      name: 'Contract Administration',
      predominantTool: 'microsoft_word',
      entries: [
        { tool: 'microsoft_word', title: 'Client Contract - Renewal Draft.docx' },
        { tool: 'microsoft_word', title: 'Vendor Agreement - Office Supplies.docx' },
        { tool: 'microsoft_excel', title: 'Contract Renewal Tracking - 2024.xlsx' },
        { tool: 'sharepoint', title: 'Legal Templates - Contracts' },
      ],
      aiNotes: {
        concise: 'Managing client contracts and vendor agreements.',
        detailed:
          'Drafting client contract renewals, preparing vendor agreements for office supplies, tracking contract renewal dates through 2024, and utilizing legal contract templates from the company repository.',
      },
    },
    {
      name: 'Event Planning',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Company Retreat - Planning Sheet.xlsx' },
        { tool: 'microsoft_word', title: 'Event Checklist - Client Appreciation Day.docx' },
        { tool: 'microsoft_outlook', title: 'Event Invitation - Client Appreciation' },
        { tool: 'sharepoint', title: 'Event Planning Resources' },
      ],
      aiNotes: {
        concise: 'Organizing company retreat and client appreciation events.',
        detailed:
          'Creating detailed planning sheets for company retreat, developing event checklists for Client Appreciation Day, drafting and sending event invitations, and utilizing event planning templates and resources.',
      },
    },
    {
      name: 'Office Management',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Office Inventory - Q2 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Facilities Maintenance Schedule.xlsx' },
        { tool: 'microsoft_word', title: 'Office Policies - 2024 Update.docx' },
        { tool: 'microsoft_teams', title: 'Chat | Facilities Management' },
      ],
      aiNotes: {
        concise: 'Managing office facilities, supplies, and operational policies.',
        detailed:
          'Maintaining office inventory records for Q2, scheduling facility maintenance activities, updating office policies documentation for 2024, and coordinating with facilities management team on operational matters.',
      },
    },
    {
      name: 'Travel Coordination',
      predominantTool: 'microsoft_outlook',
      entries: [
        { tool: 'microsoft_outlook', title: 'Travel Arrangements - Executive Team' },
        { tool: 'microsoft_excel', title: 'Travel Expense Tracking - Q2 2024.xlsx' },
        { tool: 'microsoft_word', title: 'Travel Policy - International Guidelines.docx' },
        { url: 'https://concur.com', title: 'Concur - Travel booking portal' },
      ],
      aiNotes: {
        concise: 'Arranging business travel and processing expense reports.',
        detailed:
          "Coordinating travel arrangements for executive team members, tracking travel expenses for Q2, reviewing international travel policy guidelines, and booking travel through the company's expense management portal.",
      },
    },
    {
      name: 'Meeting Coordination',
      predominantTool: 'microsoft_outlook',
      entries: [
        { tool: 'microsoft_outlook', title: 'Calendar - Board Meeting Scheduling' },
        { tool: 'microsoft_word', title: 'Meeting Agenda Template - Client Reviews.docx' },
        { tool: 'microsoft_excel', title: 'Conference Room Scheduling - July 2024.xlsx' },
        { tool: 'microsoft_teams', title: 'Chat | Meeting Coordination' },
      ],
      aiNotes: {
        concise: 'Scheduling important meetings and preparing meeting materials.',
        detailed:
          'Coordinating board meeting schedules through calendar management, creating agenda templates for client review meetings, managing conference room bookings for July, and communicating with meeting coordination team through Teams.',
      },
    },
    {
      name: 'Invoice Processing',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Invoice Tracking - Q2 2024.xlsx' },
        { tool: 'microsoft_excel', title: 'Accounts Payable Status - June 2024.xlsx' },
        { tool: 'microsoft_word', title: 'Invoice Approval Process.docx' },
        { tool: 'sharepoint', title: 'Finance - Invoice Templates' },
      ],
      aiNotes: {
        concise: 'Managing client invoices and accounts payable processes.',
        detailed:
          'Tracking invoices for Q2, monitoring accounts payable status for June, documenting invoice approval processes, and utilizing finance department invoice templates for standardized billing.',
      },
    },
    {
      name: 'Training Coordination',
      predominantTool: 'microsoft_word',
      entries: [
        { tool: 'microsoft_word', title: 'Training Materials - New Software Rollout.docx' },
        { tool: 'microsoft_excel', title: 'Training Attendance Tracking - Q2 2024.xlsx' },
        { tool: 'microsoft_powerpoint', title: 'New Hire Orientation - July 2024.pptx' },
        { tool: 'microsoft_teams', title: 'Chat | Training Department' },
      ],
      aiNotes: {
        concise: 'Organizing employee training programs and materials.',
        detailed:
          'Preparing training materials for new software rollout, tracking employee training attendance for Q2, developing presentation for July new hire orientation, and coordinating with training department on implementation plans.',
      },
    },
    {
      name: 'Policy Documentation',
      predominantTool: 'microsoft_word',
      entries: [
        { tool: 'microsoft_word', title: 'Employee Handbook - 2024 Update.docx' },
        { tool: 'microsoft_word', title: 'Information Security Policy.docx' },
        { tool: 'microsoft_word', title: 'Remote Work Guidelines - 2024.docx' },
        { tool: 'sharepoint', title: 'Human Resources - Policy Library' },
      ],
      aiNotes: {
        concise: 'Updating company policies and employee guidelines.',
        detailed:
          'Revising the employee handbook for 2024, documenting information security policies, updating remote work guidelines, and organizing policy documentation in the HR policy library.',
      },
    },
    {
      name: 'Benefits Administration',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Benefits Enrollment Tracking - 2024.xlsx' },
        { tool: 'microsoft_word', title: 'Benefits Summary - Employee Guide.docx' },
        { tool: 'microsoft_powerpoint', title: 'Benefits Overview - New Hires.pptx' },
        { tool: 'microsoft_outlook', title: 'Benefits Enrollment Reminder' },
      ],
      aiNotes: {
        concise: 'Managing employee benefits programs and enrollment.',
        detailed:
          'Tracking employee benefits enrollment for 2024, creating comprehensive benefits summary guide for employees, developing presentations on benefits options for new hires, and sending enrollment deadline reminders.',
      },
    },
    {
      name: 'Data Entry and Management',
      predominantTool: 'microsoft_excel',
      entries: [
        { tool: 'microsoft_excel', title: 'Customer Database - June 2024 Updates.xlsx' },
        { tool: 'microsoft_excel', title: 'Sales Data Compilation - Q2 2024.xlsx' },
        { tool: 'microsoft_access', title: 'Department Records Database' },
        { tool: 'microsoft_teams', title: 'Chat | Data Management' },
      ],
      aiNotes: {
        concise: 'Maintaining customer databases and compiling sales records.',
        detailed:
          'Updating customer database records for June 2024, compiling sales data for Q2 reports, managing department records in Access database, and coordinating with data management team on data integrity and maintenance processes.',
      },
    },
  ],
};
